import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { DatePicker } from '../../Components/Common/DatePicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../Components/UI/button';
import { Card, CardContent } from '../../Components/UI/card';
import { setGlobalLoading } from '../../Store/Actions';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Components/UI/tooltip';
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle } from '../../Components/UI/dialog';
import { Check, X } from 'lucide-react';
import { Checkbox } from '../../Components/UI/checkbox';
import { normalizeDateFormat } from '../../Utils/Utilities';
import { Input } from '../../Components/UI/input';
import BusinessInput from '../../Components/Common/BusinessInput';
import { Textarea } from '../../Components/UI/textarea';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../../Components/UI/select';
import { cn } from '../../Lib/Cn';
import { pluralize, validatorObj } from '../../Utils/Utilities';
import { getConnsArr } from '../../Store/Reducers';
import { saveTransactionsBulk } from './TransactionAPI';

// Common CSS classes for consistent styling
const transactionImpClasses = {
  baseInput: "w-full border sahi-radius text-sm",
  errorInput: "border-sahi-red",
  normalInput: "border-sahi-grey",
  errorHighlight: "bg-red-50",
  errorText: "text-sahi-red text-xs whitespace-normal break-words mt-1",
  button: "button-config text-base flex items-center py-[6px] px-[10px]",
  primaryButton: "sahi-primary-button",
  saveButton: "!bg-sahi-green text-base text-white py-[6px] px-[10px] sahi-radius disabled:cursor-not-allowed disabled:opacity-55 hover:!bg-sahi-white hover:!text-sahi-green border border-sahi-green",
  checkbox: "h-5 w-5 data-[state=checked]:bg-sahi-primary-color data-[state=checked]:border-sahi-primary-color data-[state=checked]:text-white transition-colors",
  card: "bg-sahi-white flex-1 flex flex-col overflow-hidden"
};

// Configuration constants for validation and business rules
const transactionConst = {
  maxFieldLen: {
    connection: 100,
    xnNo: 50,
    notes: 1000
  },
  transactionTypes: [
    'Sales Invoice',
    'Credit Note',
    'Receipt',
    'Purchase Invoice',
    'Debit Note',
    'Payment'
  ],
  // Transaction types by connection type
  transactionTypesByConnType: {
    'C': ['Sales Invoice', 'Credit Note', 'Receipt'],
    'S': ['Purchase Invoice', 'Debit Note', 'Payment'],
    'CS': ['Sales Invoice', 'Credit Note', 'Receipt', 'Purchase Invoice', 'Debit Note', 'Payment'],
    '': ['Sales Invoice', 'Credit Note', 'Receipt', 'Purchase Invoice', 'Debit Note', 'Payment']
  },
  reqFields: ['Contact', 'TransactionType', 'XnNo', 'XnDate'],
  paymentTypes: ['Payment', 'Receipt']
};

// Table cell component with memoization
const TableCell = React.memo(({ column, row, rowIndex, fieldIndex, hasError, currentError, errorMessage, renderInputField }) => {
  const isCurrentErrorCell = currentError.rowIndex === rowIndex && currentError.fieldIndex === fieldIndex;

  return (
    <td
      key={`${column.field}-${rowIndex}`}
      className={`px-4 py-3 align-top ${isCurrentErrorCell ? transactionImpClasses.errorHighlight : ""}`}
      style={{ minWidth: column.maxWidth, maxWidth: column.maxWidth }}
    >
      {renderInputField(column, row, rowIndex, fieldIndex)}
      {hasError && errorMessage && (
        <p className={transactionImpClasses.errorText}>{errorMessage}</p>
      )}
    </td>
  );
}, (prevProps, nextProps) => {
  const cellValueChanged = prevProps.row[prevProps.column.field] !== nextProps.row[nextProps.column.field];
  const errorStateChanged = prevProps.hasError !== nextProps.hasError || prevProps.errorMessage !== nextProps.errorMessage;
  const currentErrorChanged = (prevProps.currentError.rowIndex === prevProps.rowIndex && prevProps.currentError.fieldIndex === prevProps.fieldIndex) !==
    (nextProps.currentError.rowIndex === nextProps.rowIndex && nextProps.currentError.fieldIndex === nextProps.fieldIndex);

  return !cellValueChanged && !errorStateChanged && !currentErrorChanged;
});

// Table row component
const TableRow = React.memo(({ row, rowIndex, columns, errorCells, cellErrors, currentError, getCellKey, renderInputField }) => {
  return (
    <tr id={`row-${rowIndex}`} className="hover:bg-gray-50">
      {columns.map((col, fieldIndex) => {
        const cellKey = getCellKey(rowIndex, fieldIndex);
        const hasError = errorCells.has(cellKey);
        const errorMessage = cellErrors.get(cellKey);

        return (
          <TableCell
            key={`${col.field}-${rowIndex}-${col.field === 'TransactionType' ? row.Connection : ''}`}
            column={col}
            row={row}
            rowIndex={rowIndex}
            fieldIndex={fieldIndex}
            hasError={hasError}
            errorMessage={errorMessage}
            currentError={currentError}
            renderInputField={renderInputField}
          />
        );
      })}
    </tr>
  );
}, (prevProps, nextProps) => {
  const rowChanged = prevProps.row !== nextProps.row;
  const errorStateChanged = prevProps.errorCells !== nextProps.errorCells || prevProps.cellErrors !== nextProps.cellErrors;
  const currentErrorChanged = prevProps.currentError !== nextProps.currentError;

  // Force re-render when Connection changes to update TransactionType options
  const connectionChanged = prevProps.row.Connection !== nextProps.row.Connection;

  return !rowChanged && !errorStateChanged && !currentErrorChanged && !connectionChanged;
});

// Virtualized table component for performance with large datasets
function VirtualizedTable({ data, columns, errorCells, cellErrors, currentError, getCellKey, renderInputField }) {
  const parentRef = useRef();

  const rowVirtualizer = useVirtualizer({
    count: data.length + 1,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => index === 0 ? 42 : 140,
    overscan: 10,
  });

  return (
    <div className="w-full h-full max-w-full">
      <div ref={parentRef} className="h-full overflow-auto">
        <div
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualItem) => (
            <div
              key={virtualItem.key}
              className='h-full'
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {virtualItem.index === 0 ? (
                <table className="table-auto border-collapse w-full h-full">
                  <thead>
                    <tr className="bg-white shadow top-0 sticky">
                      {columns.map((col, idx) => (
                        <th
                          key={idx}
                          className="sticky top-0 z-10 px-4 py-2 text-left font-semibold border-b bg-white"
                          style={{ minWidth: col.maxWidth, maxWidth: col.maxWidth }}
                        >
                          {col.display}
                        </th>
                      ))}
                    </tr>
                  </thead>
                </table>
              ) : (
                <table className="table-auto border-collapse w-full h-full">
                  <tbody>
                    <TableRow
                      row={data[virtualItem.index - 1]}
                      rowIndex={virtualItem.index - 1}
                      columns={columns}
                      errorCells={errorCells}
                      cellErrors={cellErrors}
                      currentError={currentError}
                      getCellKey={getCellKey}
                      renderInputField={renderInputField}
                    />
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Custom hook for cell validation logic
const useTransactionValidation = (connections, transactionData) => {
  // Validates individual cell based on field type and business rules
  const validateSingleCell = useCallback((data, rowIndex, fieldIndex, columns, getCellKey) => {
    const row = data[rowIndex];
    const field = columns[fieldIndex].field;
    let errorMessage = null;

    const isPaymentOrReceipt = transactionConst.paymentTypes.includes(row.TransactionType);

    const validationRules = {
      Connection: () => {
        if (!row.Connection?.trim()) return 'Contact is required';
        if (row.Connection.length > transactionConst.maxFieldLen.connection) return 'Contact name is too long';
        const connection = connections.find(c => c.BizName?.toLowerCase() === row.Connection.toLowerCase());
        if (!connection) return 'Please select a valid contact';
        return null;
      },

      TransactionType: () => {
        if (!row.TransactionType?.trim()) return 'Transaction type is required';

        // Get connection type for validation
        const connection = connections.find(c => c.BizName?.toLowerCase() === row.Connection?.toLowerCase());
        const connType = connection?.ConnType || '';

        // Get allowed transaction types for this connection type
        const allowedTypes = transactionConst.transactionTypesByConnType[connType] || transactionConst.transactionTypesByConnType[''];

        if (!allowedTypes.includes(row.TransactionType)) {
          return 'Please select a valid transaction type';
        }

        return null;
      },

      XnNo: () => {
        if (!row.XnNo?.trim()) return 'Transaction number is required';
        if (row.XnNo.length > transactionConst.maxFieldLen.xnNo) return 'Transaction number is too long';

        // Check for duplicates in current data
        if (data.some((otherRow, otherIndex) =>
          otherIndex !== rowIndex &&
          otherRow.XnNo === row.XnNo &&
          otherRow.TransactionType === row.TransactionType
        )) {
          return 'Duplicate transaction number for this transaction type';
        }
        return null;
      },

      PreTaxAmount: () => {
        if (isPaymentOrReceipt) return null; // Not required for Payment/Receipt

        if (!row.PreTaxAmount) return 'Pre-tax amount is required for this transaction type';
        const amount = parseFloat(row.PreTaxAmount);
        if (isNaN(amount) || amount <= 0) return 'Pre-tax amount must be a positive number';
        return null;
      },
      TotalAmount: () => {
        if (!row.TotalAmount) return 'Total Amount is required';
        const amount = parseFloat(row.TotalAmount);
        if (isNaN(amount) || amount <= 0) return 'Total Amount must be a positive number';

        // For non-payment/receipt, total should be >= pre-tax
        if (!isPaymentOrReceipt && row.PreTaxAmount) {
          const preTaxAmount = parseFloat(row.PreTaxAmount);
          if (!isNaN(preTaxAmount) && amount < preTaxAmount) {
            return 'Total amount cannot be less than pre-tax amount';
          }
        }
        return null;
      },

      XnDate: () => {
        if (!row.XnDate?.trim()) return 'Transaction date is required';

        const xnDate = new Date(row.XnDate);
        const today = new Date();
        const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        if (isNaN(xnDate.getTime())) return 'Invalid date format';

        const xnDateOnly = new Date(xnDate.getFullYear(), xnDate.getMonth(), xnDate.getDate());
        if (xnDateOnly > todayStart) {
          return 'Transaction date cannot be in the future';
        }

        const dateValidation = validatorObj.validateDate(row.XnDate, { allowFuture: false, isRequired: true });
        return dateValidation;
      },


      Notes: () => {
        if (row.Notes?.trim() && row.Notes.length > transactionConst.maxFieldLen.notes) {
          return 'Notes are too long (max ' + transactionConst.maxFieldLen.notes + ' characters)';
        }
        return null;
      }
    };

    if (validationRules[field]) {
      errorMessage = validationRules[field]();
    }

    return { cellKey: getCellKey(rowIndex, fieldIndex), errorMessage };
  }, [connections, transactionData]);

  return { validateSingleCell };
};

// Custom hook for managing cell focus and navigation
const useFocusManagement = (editableData, columns) => {
  const [currentFocusedCell, setCurrentFocusedCell] = useState({ rowIndex: -1, fieldIndex: -1 });
  const [previousCell, setPreviousCell] = useState({ rowIndex: -1, fieldIndex: -1 });

  // Helper function to check if a field is disabled
  const isFieldDisabled = useCallback((rowIndex, fieldIndex) => {
    const row = editableData[rowIndex];
    const field = columns[fieldIndex]?.field;

    if (field === "PreTaxAmount") {
      const isPaymentOrReceipt = transactionConst.paymentTypes.includes(row?.TransactionType);
      return isPaymentOrReceipt; // Only disable if it's a Payment/Receipt transaction
    }

    return false; // Ensure other fields (like TotalAmount) are not disabled by default
  }, [editableData, columns]);

  // Calculates next cell position for tab navigation
  const getNextCell = useCallback((currentRowIndex, currentFieldIndex, direction = "forward") => {
    const totalRows = editableData.length;
    const totalFields = columns.length;
    let nextRowIndex = currentRowIndex;
    let nextFieldIndex = currentFieldIndex;
    let attempts = 0;
    const maxAttempts = totalRows * totalFields; // Prevent infinite loops

    do {
      if (direction === "forward") {
        nextFieldIndex++;
        if (nextFieldIndex >= totalFields) {
          nextFieldIndex = 1; // Start at field index 1 (skip "No" column) instead of 0
          nextRowIndex++;
          if (nextRowIndex >= totalRows) {
            nextRowIndex = 0; // Wrap to the first row if at the end
          }
        }
      } else {
        nextFieldIndex--;
        if (nextFieldIndex < 0) {
          nextFieldIndex = totalFields - 1; // Wrap to the last column
          nextRowIndex--;
          if (nextRowIndex < 0) {
            nextRowIndex = totalRows - 1; // Wrap to the last row
          }
        }

        // Skip the "No" column (fieldIndex 0) when wrapping backwards
        if (nextFieldIndex === 0) {
          nextFieldIndex = totalFields - 1; // Go to the last field instead
          nextRowIndex--;
          if (nextRowIndex < 0) {
            nextRowIndex = totalRows - 1;
          }
        }
      }

      // Skip the "No" column (fieldIndex 0) as it's not editable
      if (nextFieldIndex === 0) {
        continue;
      }

      attempts++;

      // If we've tried all possibilities, break to prevent infinite loop
      if (attempts >= maxAttempts) {
        break;
      }

      // Check if this field is disabled, if so continue to next
    } while (isFieldDisabled(nextRowIndex, nextFieldIndex));

    return { rowIndex: nextRowIndex, fieldIndex: nextFieldIndex };
  }, [editableData.length, columns.length, isFieldDisabled]);

  return { currentFocusedCell, setCurrentFocusedCell, previousCell, setPreviousCell, getNextCell };
};

// Main component
const CorrectExcelTransactions = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { excelData: initialExcelData = [], transactionData = [] } = location.state || {};

  // State management
  const [editableData, setEditableData] = useState([]);
  const [errorCells, setErrorCells] = useState(new Set());
  const [cellErrors, setCellErrors] = useState(new Map());
  const [currentError, setCurrentError] = useState({ rowIndex: -1, fieldIndex: -1 });
  const [saveDialog, setSaveDialog] = useState(false);
  const [ignoreErrors, setIgnoreErrors] = useState(false);
  const [apiError, setAPIError] = useState(null);

  const connections = useSelector(getConnsArr);
  const inputRefs = useRef([]);

  const { validateSingleCell } = useTransactionValidation(connections, transactionData);

  const columns = useMemo(() => [
    { field: 'No', maxWidth: '50px', type: 'text', display: 'No' },
    { field: 'Contact', maxWidth: '200px', type: 'select', display: <>Contact <span className="text-red-500">*</span></> },
    { field: 'TransactionType', maxWidth: '150px', type: 'select', display: <>Transaction Type <span className="text-red-500">*</span></> },
    { field: 'XnNo', maxWidth: '150px', type: 'text', display: <>Transaction No <span className="text-red-500">*</span></> },
    { field: 'PreTaxAmount', maxWidth: '150px', type: 'number', display: 'Pre-Tax Amount' },
    { field: 'TotalAmount', maxWidth: '150px', type: 'number', display: <>Total Amount <span className="text-red-500">*</span></> },
    { field: 'XnDate', maxWidth: '140px', type: 'date', display: <>Transaction Date <span className="text-red-500">*</span></> },
    { field: 'Notes', maxWidth: '250px', type: 'textarea', display: 'Notes' }
  ], []);

  const { currentFocusedCell, setCurrentFocusedCell, previousCell, setPreviousCell, getNextCell } = useFocusManagement(editableData, columns);
  // Utility functions
  const getCellKey = useCallback((rowIndex, fieldIndex) => `${rowIndex},${fieldIndex}`, []);

  // Gets dependent cells that need revalidation when current cell changes
  const getDependentCells = useCallback((rowIndex, fieldIndex, field) => {
    const dependents = [];
    const dependencyMap = {
      TransactionType: ['PreTaxAmount', 'TotalAmount'], // When transaction type changes, revalidate amounts
      PreTaxAmount: ['TotalAmount'] // When pre-tax changes, revalidate total
    };

    if (!dependencyMap[field]) {
      return dependents;
    }

    if (dependencyMap[field]) {
      dependencyMap[field].forEach(depField => {
        const depFieldIndex = columns.findIndex(col => col.field === depField);
        if (depFieldIndex !== -1) {
          dependents.push({ rowIndex, fieldIndex: depFieldIndex });
        }
      });
    }

    return dependents;
  }, [columns]);

  // Initialize data when component mounts
  useEffect(() => {
    const initializeData = async () => {
      dispatch(setGlobalLoading(true));

      try {
        if (initialExcelData?.length > 0) {
          // Pre-process data to match transaction types and connections
          const sanitizedData = initialExcelData.map(row => {
            const processedRow = {};

            // Trim all values first and normalize dates
            Object.keys(row).forEach(key => {
              const value = row[key];
              if (typeof value === 'string') {
                const trimmedValue = value.trim();
                if ((key === 'XnDate' || key === 'LastRecordDT' || key === 'NextTaskDT') && trimmedValue) {
                  processedRow[key] = normalizeDateFormat(trimmedValue);
                } else {
                  processedRow[key] = trimmedValue;
                }
              } else {
                processedRow[key] = value;
              }
            });

            // Pre-match Connection
            if (processedRow.Connection && connections.length > 0) {
              const normalizedConnection = processedRow.Connection.toLowerCase();
              const matchedConnection = connections.find(conn =>
                conn.BizName && conn.BizName.trim().toLowerCase() === normalizedConnection
              );
              if (matchedConnection) {
                processedRow.Connection = matchedConnection.BizName;
                processedRow.ConnectionID = matchedConnection.ConnID || matchedConnection.connID || 0;
              }
            }

            // Pre-match Transaction Type based on connection's ConnType
            if (processedRow.TransactionType) {
              const normalizedType = processedRow.TransactionType.toLowerCase();
              const connection = connections.find(c => c.BizName?.toLowerCase() === processedRow.Connection?.toLowerCase());
              const connType = connection?.ConnType || '';
              const allowedTypes = transactionConst.transactionTypesByConnType[connType] || transactionConst.transactionTypesByConnType[''];

              const matchedType = allowedTypes.find(type =>
                type.trim().toLowerCase() === normalizedType
              );
              if (matchedType) {
                processedRow.TransactionType = matchedType;
              }
            }

            return processedRow;
          });

          setEditableData(sanitizedData);
          inputRefs.current = Array(sanitizedData.length).fill()
            .map(() => Array(columns.length).fill().map(() => React.createRef()));

          await new Promise((resolve) => {
            setTimeout(() => {
              validateAllData(sanitizedData);
              resolve();
            }, 100);
          });
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        setAPIError('Failed to initialize data');
      } finally {
        dispatch(setGlobalLoading(false));
      }
    };

    initializeData();
  }, [initialExcelData, connections]); // Added connections to dependency array

  // Validates data and updates error states
  const validateData = useCallback((data, cellCoordinate = null) => {
    let newErrorCells = new Set();
    let newCellErrors = new Map();

    if (cellCoordinate === null) {
      // Full validation - clear everything first
      data.forEach((row, rowIndex) => {
        columns.forEach((col, fieldIndex) => {
          if (col.field !== 'No') {
            const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex, columns, getCellKey);
            if (errorMessage) {
              newErrorCells.add(cellKey);
              newCellErrors.set(cellKey, errorMessage);
            }
          }
        });
      });
    } else {
      // Single cell validation - start with current state
      setErrorCells(currentErrorCells => {
        setCellErrors(currentCellErrors => {
          newErrorCells = new Set(currentErrorCells);
          newCellErrors = new Map(currentCellErrors);

          const { rowIndex, fieldIndex } = cellCoordinate;
          const field = columns[fieldIndex].field;

          const { cellKey, errorMessage } = validateSingleCell(data, rowIndex, fieldIndex, columns, getCellKey);

          if (errorMessage) {
            newErrorCells.add(cellKey);
            newCellErrors.set(cellKey, errorMessage);
          } else {
            newErrorCells.delete(cellKey);
            newCellErrors.delete(cellKey);
          }

          const dependentCells = getDependentCells(rowIndex, fieldIndex, field);
          dependentCells.forEach(({ rowIndex: depRowIndex, fieldIndex: depFieldIndex }) => {
            const { cellKey: depCellKey, errorMessage: depErrorMessage } =
              validateSingleCell(data, depRowIndex, depFieldIndex, columns, getCellKey);

            if (depErrorMessage) {
              newErrorCells.add(depCellKey);
              newCellErrors.set(depCellKey, depErrorMessage);
            } else {
              newErrorCells.delete(depCellKey);
              newCellErrors.delete(depCellKey);
            }
          });

          // Handle InvoiceNo duplicate validation
          if (field === 'XnNo' || field === 'TransactionType') {
            const duplicateMessage = 'Duplicate Transaction Number for this transaction type';

            for (const cellKey of newErrorCells) {
              if (newCellErrors.get(cellKey) === duplicateMessage) {
                newErrorCells.delete(cellKey);
                newCellErrors.delete(cellKey);
              }
            }

            const invoiceNoFieldIndex = columns.findIndex(col => col.field === 'XnNo');
            const invoiceTypeMap = new Map();

            data.forEach((row, rowIDX) => {
              const invoiceNo = row.XnNo?.trim();
              const transactionType = row.TransactionType?.trim();
              if (invoiceNo && transactionType) {
                const key = `${invoiceNo}-${transactionType}`;
                if (!invoiceTypeMap.has(key)) {
                  invoiceTypeMap.set(key, []);
                }
                invoiceTypeMap.get(key).push(rowIDX);
              }
            });

            invoiceTypeMap.forEach((rowIndices) => {
              if (rowIndices.length > 1) {
                rowIndices.forEach(rowIDX => {
                  const cellKey = getCellKey(rowIDX, invoiceNoFieldIndex);
                  newErrorCells.add(cellKey);
                  newCellErrors.set(cellKey, duplicateMessage);
                });
              }
            });
          }

          return newCellErrors;
        });
        return newErrorCells;
      });
    }

    // Update states for full validation
    if (cellCoordinate === null) {
      setErrorCells(newErrorCells);
      setCellErrors(newCellErrors);
    }

    // Handle currentError update
    const errorCellsArray = Array.from(newErrorCells);
    if (errorCellsArray.length > 0 &&
      (currentError.rowIndex === -1 || !newErrorCells.has(getCellKey(currentError.rowIndex, currentError.fieldIndex)))) {
      const firstErrorCell = errorCellsArray[0];
      const [rowIndex, fieldIndex] = firstErrorCell.split(',').map(Number);
      setCurrentError({ rowIndex, fieldIndex });
    } else if (errorCellsArray.length === 0) {
      setCurrentError({ rowIndex: -1, fieldIndex: -1 });
    }
  }, [currentError, columns, validateSingleCell, getCellKey, getDependentCells]);

  const validateAllData = useCallback((data) => {
    validateData(data, null);
  }, [validateData]);

  const handleInputChange = useCallback((index, fieldIndex, value) => {
    const inputRef = inputRefs.current[index][fieldIndex].current;
    const cursorPosition = inputRef?.selectionStart || null;
    const field = columns[fieldIndex].field;

    setEditableData((prevData) => {
      const currentValue = prevData[index][field];
      if (currentValue === value) return prevData;

      const newRow = { ...prevData[index] };
      
      // Normalize date fields when user enters them
      if ((field === 'XnDate' || field === 'LastRecordDT' || field === 'NextTaskDT') && value) {
        newRow[field] = normalizeDateFormat(value);
      } else {
        newRow[field] = value || "";
      }

      if (field === "Contact") {
        const connection = connections.find(c => c.BizName === value);
        newRow.ConnectionID = connection ? (connection.ConnID || connection.connID || 0) : 0;

        // Clear transaction type if it's not valid for the new connection type
        if (newRow.TransactionType) {
          const connType = connection?.ConnType || '';
          const allowedTypes = transactionConst.transactionTypesByConnType[connType] || transactionConst.transactionTypesByConnType[''];

          if (!allowedTypes.includes(newRow.TransactionType)) {
            newRow.TransactionType = "";
          }
        }
      }

      // Clear PreTaxAmount when changing to Payment/Receipt transaction type
      if (field === "TransactionType") {
        const isPaymentOrReceipt = transactionConst.paymentTypes.includes(value);
        if (isPaymentOrReceipt) {
          newRow.PreTaxAmount = ""; // Clear the PreTaxAmount field
        }
      }

      const updatedData = [...prevData];
      updatedData[index] = newRow;

      // Clear validation errors immediately for changed field
      const cellKey = getCellKey(index, fieldIndex);
      setErrorCells(currentErrorCells => {
        const newErrorCells = new Set(currentErrorCells);
        newErrorCells.delete(cellKey);

        setCellErrors(currentCellErrors => {
          const newCellErrors = new Map(currentCellErrors);
          newCellErrors.delete(cellKey);
          return newCellErrors;
        });

        return newErrorCells;
      });

      // Clear duplicate validation errors immediately for InvoiceNo
      if (field === "XnNo") {
        const duplicateMessage = 'Duplicate Transaction Number for this transaction type';

        setErrorCells(currentErrorCells => {
          const newErrorCells = new Set(currentErrorCells);

          setCellErrors(currentCellErrors => {
            const newCellErrors = new Map(currentCellErrors);

            for (const cellKey of newErrorCells) {
              if (newCellErrors.get(cellKey) === duplicateMessage) {
                newErrorCells.delete(cellKey);
                newCellErrors.delete(cellKey);
              }
            }

            return newCellErrors;
          });

          return newErrorCells;
        });
      }
      return updatedData;
    });

    if (cursorPosition !== null && (inputRef?.type === "text" || inputRef?.tagName === "TEXTAREA")) {
      requestAnimationFrame(() => {
        if (inputRef && inputRef.selectionStart !== undefined) {
          inputRef.setSelectionRange(cursorPosition, cursorPosition);
        }
      });
    }
  }, [columns, connections, getCellKey]);

  const handleInputBlur = useCallback((index, fieldIndex) => {
    setTimeout(() => {
      setEditableData(currentData => {
        validateData(currentData, { rowIndex: index, fieldIndex });
        return currentData;
      });
    }, 100);
  }, [validateData]);

  // Gets sorted array of error cells for navigation
  const getSortedErrorCells = useCallback(() => {
    return Array.from(errorCells)
      .map(cellKey => {
        const [rowIndex, fieldIndex] = cellKey.split(',').map(Number);
        return { rowIndex, fieldIndex, cellKey };
      })
      .sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) {
          return a.rowIndex - b.rowIndex;
        }
        return a.fieldIndex - b.fieldIndex;
      });
  }, [errorCells]);

  // Navigation helper functions for error cells
  const findNextErrorAfterPosition = useCallback((currentRowIndex, currentFieldIndex) => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return null;

    const nextError = sortedErrors.find(error => {
      if (error.rowIndex > currentRowIndex) return true;
      if (error.rowIndex === currentRowIndex && error.fieldIndex > currentFieldIndex) return true;
      return false;
    });

    return nextError || sortedErrors[0];
  }, [getSortedErrorCells]);

  const findPreviousErrorBeforePosition = useCallback((currentRowIndex, currentFieldIndex) => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return null;

    let previousError = null;
    for (let i = sortedErrors.length - 1; i >= 0; i--) {
      const error = sortedErrors[i];
      if (error.rowIndex < currentRowIndex) {
        previousError = error;
        break;
      }
      if (error.rowIndex === currentRowIndex && error.fieldIndex < currentFieldIndex) {
        previousError = error;
        break;
      }
    }

    return previousError || sortedErrors[sortedErrors.length - 1];
  }, [getSortedErrorCells]);

  // Finds currently active element in the table for focus management
  const findActiveElement = useCallback((activeElement) => {
    if (!activeElement || !inputRefs.current.length) return null;

    for (let rowIndex = 0; rowIndex < inputRefs.current.length; rowIndex++) {
      const row = inputRefs.current[rowIndex];
      for (let fieldIndex = 0; fieldIndex < row.length; fieldIndex++) {
        const ref = row[fieldIndex];
        if (ref?.current === activeElement || ref?.current?.contains?.(activeElement)) {
          return { rowIndex, fieldIndex };
        }
      }
    }

    if (activeElement?.id?.startsWith('date-') || activeElement?.closest('[id^="date-"]')) {
      const dateElement = activeElement?.id?.startsWith('date-') ?
        activeElement : activeElement.closest('[id^="date-"]');

      if (dateElement?.id) {
        const match = dateElement.id.match(/date-(\d+)-(\d+)/);
        if (match) {
          return { rowIndex: parseInt(match[1]), fieldIndex: parseInt(match[2]) };
        }
      }
    }

    let element = activeElement;
    while (element && element.tagName !== 'TD') {
      element = element.parentElement;
      if (!element || element.tagName === 'BODY') break;
    }

    if (element?.tagName === 'TD') {
      const row = element.closest('tr[id^="row-"]');
      if (row?.id) {
        const rowMatch = row.id.match(/^row-(\d+)$/);
        if (rowMatch) {
          const rowIndex = parseInt(rowMatch[1]);
          const cells = Array.from(row.children);
          const fieldIndex = cells.indexOf(element);
          if (fieldIndex !== -1) {
            return { rowIndex, fieldIndex };
          }
        }
      }
    }

    return null;
  }, []);

  const focusCell = useCallback((rowIndex, fieldIndex) => {
    const field = columns[fieldIndex]?.field;
    let targetRef = inputRefs.current[rowIndex]?.[fieldIndex]?.current;

    if (field === "XnDate") {
      if (!targetRef) {
        const dateContainer = document.querySelector(`#date-${rowIndex}-${fieldIndex}`);
        if (dateContainer) {
          const button = dateContainer.querySelector("button");
          if (button) {
            targetRef = button;
          }
        }
      }
    } else if (field === "Contact" || field === "TransactionType") {
      if (targetRef) {
        const selectTrigger =
          targetRef.querySelector('[role="combobox"]') ||
          targetRef.querySelector('button[role="combobox"]') ||
          targetRef.querySelector("button") ||
          targetRef;

        if (selectTrigger && selectTrigger !== targetRef) {
          targetRef = selectTrigger;
        }
      }
    } else if (field === "PreTaxAmount" || field === "TotalAmount") {
      // For BusinessInput, focus the container div which will delegate to the input
      if (targetRef && targetRef.classList.contains("sahi-input")) {
        // BusinessInput container is already the correct target
      }
    }

    if (targetRef) {
      setPreviousCell({
        rowIndex: currentFocusedCell.rowIndex,
        fieldIndex: currentFocusedCell.fieldIndex,
      });

      setCurrentFocusedCell({ rowIndex, fieldIndex });

      const doFocus = () => {
        try {
          if (targetRef && typeof targetRef.focus === "function") {
            targetRef.focus();

            if (
              (targetRef.type === "text" || targetRef.tagName === "TEXTAREA") &&
              typeof targetRef.select === "function"
            ) {
              targetRef.select();
            } else if (targetRef.classList.contains("sahi-input")) {
              // For BusinessInput, focus the inner input when the div is focused
              const inputElement = targetRef.querySelector("input");
              if (inputElement && typeof inputElement.focus === "function") {
                inputElement.focus();
                if (typeof inputElement.select === "function") {
                  inputElement.select();
                }
              }
            }
          }
        } catch (error) {
          console.error("Focus error:", error);
        }
      };

      doFocus();
      requestAnimationFrame(() => {
        if (document.activeElement !== targetRef && document.activeElement !== targetRef.querySelector("input")) {
          doFocus();
        }
      });

      return true;
    }

    return false;
  }, [currentFocusedCell, columns]);

  // Scrolls to and highlights error cell
  const scrollToError = useCallback((rowIndex, fieldIndex) => {
    if (fieldIndex === -1) return;
    const field = columns[fieldIndex].field;
    const target =
      field === "XnDate"
        ? document.getElementById(`date-${rowIndex}-${fieldIndex}`)
        : inputRefs.current[rowIndex]?.[fieldIndex]?.current;

    if (!target) return;

    inputRefs.current.flat().forEach(ref => ref?.current?.classList.remove("error-highlight"));
    document.querySelectorAll('[id^="date-"]').forEach(el => el.classList.remove("error-highlight"));

    target.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest"
    });
    target.classList.add("error-highlight");
  }, [columns]);

  // Navigates to next error in the table
  const handleNextError = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return;

    let nextError;
    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) {
      nextError = sortedErrors[0];
    } else {
      nextError = findNextErrorAfterPosition(referenceRow, referenceField);
    }

    if (nextError) {
      setCurrentError({ rowIndex: nextError.rowIndex, fieldIndex: nextError.fieldIndex });
      scrollToError(nextError.rowIndex, nextError.fieldIndex);
      setCurrentFocusedCell({
        rowIndex: nextError.rowIndex,
        fieldIndex: nextError.fieldIndex
      });
      focusCell(nextError.rowIndex, nextError.fieldIndex);
    }
  }, [currentFocusedCell, currentError, getSortedErrorCells, findNextErrorAfterPosition, scrollToError, focusCell]);

  // Navigates to previous error in the table
  const handlePreviousError = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return;

    let previousError;
    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) {
      previousError = sortedErrors[sortedErrors.length - 1];
    } else {
      previousError = findPreviousErrorBeforePosition(referenceRow, referenceField);
    }

    if (previousError) {
      setCurrentError({ rowIndex: previousError.rowIndex, fieldIndex: previousError.fieldIndex });
      scrollToError(previousError.rowIndex, previousError.fieldIndex);
      setCurrentFocusedCell({
        rowIndex: previousError.rowIndex,
        fieldIndex: previousError.fieldIndex
      });
      focusCell(previousError.rowIndex, previousError.fieldIndex);
    }
  }, [currentFocusedCell, currentError, getSortedErrorCells, findPreviousErrorBeforePosition, scrollToError, focusCell]);

  // Gets rows without any validation errors
  const getErrorFreeRows = useCallback(() => {
    const errorFreeRows = [];
    const errorRowIndices = new Set();

    errorCells.forEach(cellKey => {
      const [rowIndex] = cellKey.split(',').map(Number);
      errorRowIndices.add(rowIndex);
    });

    editableData.forEach((row, index) => {
      if (!errorRowIndices.has(index)) {
        errorFreeRows.push({
          ...row,
          originalIndex: index
        });
      }
    });

    return errorFreeRows;
  }, [editableData, errorCells]);

  const errorFreeRows = useMemo(() => getErrorFreeRows(), [getErrorFreeRows]);

  // Save function - Placeholder for transaction saving
  const handleSave = useCallback(async () => {
    const dataToSave = ignoreErrors ? errorFreeRows : editableData;

    if (!ignoreErrors && errorCells.size > 0) {
      toast.error(`Please fix ${errorCells.size} error(s) before saving.`);
      return;
    }

    if (ignoreErrors && dataToSave.length === 0) {
      toast.error('No valid rows to save. All rows have errors.');
      return;
    }

    setAPIError(null);

    try {
      dispatch(setGlobalLoading(true));

      const transactionParams = dataToSave.map((row) => ({
        TxnID: 0,
        ConnID: row.ConnectionID || 0,
        XnTypID: String(row.TransactionType || ''), // TODO : TYPEID
        XnNo: String(row.XnNo || '').substring(0, 50),
        PTAmt: parseFloat(row.PreTaxAmount) || 0,
        TotalAmt: parseFloat(row.TotalAmount) || 0,
        XnDate: validatorObj.formatSmallDateTime(row.XnDate),
        Notes: String(row.Notes || '').substring(0, 1000),
      }));
      // const transactionParams = dataToSave.map((row) => ({
      //   ConnectionID: row.ConnectionID || 0,
      //   Connection: String(row.Connection || '').substring(0, 100),
      //   TransactionType: String(row.TransactionType || ''),
      //   XnNo: String(row.XnNo || '').substring(0, 50),
      //   PreTaxAmount: parseFloat(row.PreTaxAmount) || 0,
      //   TotalAmount: parseFloat(row.TotalAmount) || 0,
      //   XnDate: validatorObj.formatSmallDateTime(row.XnDate),
      //   Notes: String(row.Notes || '').substring(0, 1000),
      //   Status: 'Active',
      //   CreatedDate: validatorObj.formatSmallDateTime(new Date())
      // }));

      const cleanedParams = transactionParams.map(param => {
        const cleaned = {};
        Object.keys(param).forEach(key => {
          const value = param[key];
          if (value !== undefined && value !== null && value !== 'null') {
            cleaned[key] = value;
          }
        });
        return cleaned;
      });

      const result = await saveTransactionsBulk(cleanedParams);
      if (!result.success) {
        throw new Error(result.message || 'Transaction import failed');
      }

      setSaveDialog(false);

      const successMessage = ignoreErrors && dataToSave.length < editableData.length
        ? `${dataToSave.length} transactions imported. ${editableData.length - dataToSave.length} rows with errors were skipped.`
        : 'Transactions imported.';

      toast.success(successMessage);

      setTimeout(() => {
        navigate('/transactions');
      }, 3000);

    } catch (err) {
      setAPIError(err.message);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  }, [ignoreErrors, errorFreeRows, editableData, errorCells.size, dispatch, navigate]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;

      const activeElement = document.activeElement;
      const currentCell = findActiveElement(activeElement);

      if (currentCell) {
        e.preventDefault();

        let nextCell = getNextCell(
          currentCell.rowIndex,
          currentCell.fieldIndex,
          e.shiftKey ? "backward" : "forward"
        );

        if (nextCell) {
          focusCell(nextCell.rowIndex, nextCell.fieldIndex);
        }
      } else if (inputRefs.current.length > 0) {
        e.preventDefault();
        focusCell(0, 1); // Start at the first focusable field (skip "No")
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [findActiveElement, getNextCell, focusCell]);

  // Focus tracking for current cell management
  useEffect(() => {
    const handleFocusIn = (e) => {
      const activeElement = e.target;
      const currentCell = findActiveElement(activeElement);

      if (currentCell && (
        currentCell.rowIndex !== currentFocusedCell.rowIndex ||
        currentCell.fieldIndex !== currentFocusedCell.fieldIndex
      )) {
        setPreviousCell({
          rowIndex: currentFocusedCell.rowIndex,
          fieldIndex: currentFocusedCell.fieldIndex
        });

        setCurrentFocusedCell({
          rowIndex: currentCell.rowIndex,
          fieldIndex: currentCell.fieldIndex
        });

        const cellKey = getCellKey(currentCell.rowIndex, currentCell.fieldIndex);
        const hasError = errorCells.has(cellKey);
        if (hasError) {
          setCurrentError({ rowIndex: currentCell.rowIndex, fieldIndex: currentCell.fieldIndex });
        } else {
          setCurrentError({ rowIndex: -1, fieldIndex: -1 });
        }
      }
    };

    document.addEventListener("focusin", handleFocusIn);
    return () => document.removeEventListener("focusin", handleFocusIn);
  }, [currentFocusedCell, findActiveElement, errorCells, getCellKey]);

  // Button state helpers for error navigation
  const getErrorNavigationButtonStates = useCallback(() => {
    const sortedErrors = getSortedErrorCells();
    if (sortedErrors.length === 0) return { nextDisabled: true, prevDisabled: true };

    const referenceRow = currentFocusedCell.rowIndex !== -1 ? currentFocusedCell.rowIndex : currentError.rowIndex;
    const referenceField = currentFocusedCell.fieldIndex !== -1 ? currentFocusedCell.fieldIndex : currentError.fieldIndex;

    if (referenceRow === -1 || referenceField === -1) return { nextDisabled: false, prevDisabled: false };

    const nextError = findNextErrorAfterPosition(referenceRow, referenceField);
    const previousError = findPreviousErrorBeforePosition(referenceRow, referenceField);

    const nextDisabled = nextError === sortedErrors[0] && (
      referenceRow > sortedErrors[sortedErrors.length - 1].rowIndex ||
      (referenceRow === sortedErrors[sortedErrors.length - 1].rowIndex &&
        referenceField >= sortedErrors[sortedErrors.length - 1].fieldIndex)
    );

    const currentErrorIndex = sortedErrors.findIndex(error =>
      error.rowIndex === referenceRow && error.fieldIndex === referenceField
    );
    const prevDisabled = currentErrorIndex === 0 || (currentErrorIndex === -1 &&
      (referenceRow < sortedErrors[0].rowIndex ||
        (referenceRow === sortedErrors[0].rowIndex && referenceField <= sortedErrors[0].fieldIndex)));

    return { nextDisabled, prevDisabled };
  }, [getSortedErrorCells, currentFocusedCell, currentError, findNextErrorAfterPosition, findPreviousErrorBeforePosition]);

  const { nextDisabled, prevDisabled } = getErrorNavigationButtonStates();

  const triggerValidation = useCallback((rowIndex, fieldIndex) => {
    requestAnimationFrame(() => {
      setEditableData(currentData => {
        validateData(currentData, { rowIndex, fieldIndex });
        return currentData;
      });
    });
  }, [validateData]);

  // Renders appropriate input field based on column type
  const renderInputField = useCallback((column, row, rowIndex, fieldIndex) => {
    const value = column.field === "No"
      ? (rowIndex + 1).toString()
      : row[column.field] || "";

    const cellKey = getCellKey(rowIndex, fieldIndex);
    const hasError = errorCells.has(cellKey);
    const baseClassName = `${transactionImpClasses.baseInput} ${hasError ? transactionImpClasses.errorInput : transactionImpClasses.normalInput}`;
    const commonProps = {
      ref: inputRefs.current[rowIndex][fieldIndex],
      value,
      onChange: (e) => handleInputChange(rowIndex, fieldIndex, e.target.value),
      className: baseClassName,
    };

    // Special props for non-BusinessInput components
    const inputProps = {
      ...commonProps,
      className: baseClassName,
    };

    const isPaymentOrReceipt = transactionConst.paymentTypes.includes(row.TransactionType);

    const fieldRenderers = {
      select: () => {
        let options = [];
        let normalizedValue = value ? value.trim().toLowerCase() : "";
        let matchedOption = "";

        if (column.field === "Contact") {
          options = connections.map(c => c.BizName).filter(Boolean);
          // Find matching connection by normalized comparison
          if (normalizedValue) {
            const foundConnection = connections.find(c =>
              c.BizName && c.BizName.trim().toLowerCase() === normalizedValue
            );
            matchedOption = foundConnection ? foundConnection.BizName : value;
          }
        } else if (column.field === "TransactionType") {
          // Filter transaction types based on selected connection's ConnType
          const connection = connections.find(c => c.BizName?.toLowerCase() === row.Connection?.toLowerCase());
          const connType = connection?.ConnType || '';
          options = transactionConst.transactionTypesByConnType[connType] || transactionConst.transactionTypesByConnType[''];

          // Find matching transaction type by normalized comparison
          if (normalizedValue) {
            const foundType = options.find(type =>
              type.trim().toLowerCase() === normalizedValue
            );
            matchedOption = foundType || value;
          }
        }

        const selectValue = matchedOption || value || "__none__";

        return (
          <Select
            value={selectValue}
            onValueChange={(val) => {
              handleInputChange(rowIndex, fieldIndex, val === "__none__" ? "" : val);
              setTimeout(() => handleInputBlur(rowIndex, fieldIndex), 0);
            }}
          >
            <SelectTrigger
              ref={inputRefs.current[rowIndex][fieldIndex]}
              className={cn("sahi-input", baseClassName)}
              tabIndex={0}
            >
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value="__none__">Select</SelectItem>
                {options.map((option, i) => (
                  <SelectItem key={i} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        );
      },

      number: () => {
        // Get the current transaction type from the row data, not just the initial value
        const currentTransactionType = row.TransactionType || "";
        const isDisabled = column.field === "PreTaxAmount" &&
          transactionConst.paymentTypes.includes(currentTransactionType);

        const hasError = errorCells.has(getCellKey(rowIndex, fieldIndex));

        // Clear PreTaxAmount value when disabling the field
        const displayValue = isDisabled ? "" : value;

        return (
          <BusinessInput
            fieldID={`amount-${rowIndex}-${fieldIndex}`}
            value={displayValue}
            onChange={(e) => handleInputChange(rowIndex, fieldIndex, e.target.value)}
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
            disabled={isDisabled}
            className={`${hasError ? "border-red-500" : ""} ${isDisabled ? "bg-gray-100 opacity-50" : ""}`}
            ref={inputRefs.current[rowIndex][fieldIndex]}
            maxIntegerDigits={10}
            maxDecimalPlaces={2}
            placeholder="0.00"
          />
        );
      },

      textarea: () => (
        <Textarea
          {...inputProps}
          className={`${baseClassName} resize-none`}
          rows={2}
          placeholder=""
          onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
        />
      ),

      date: () => {
        let dateValue = "";
        if (value) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) {
            try {
              const year = d.getFullYear();
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const day = String(d.getDate()).padStart(2, '0');
              const hours = String(d.getHours()).padStart(2, '0');
              const minutes = String(d.getMinutes()).padStart(2, '0');
              const seconds = String(d.getSeconds()).padStart(2, '0');
              dateValue = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
            } catch {
              dateValue = "";
            }
          }
        }

        const today = new Date();
        const maxDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

        return (
          <div id={`date-${rowIndex}-${fieldIndex}`}>
            <DatePicker
              value={dateValue}
              onChange={(date) => {
                if (date) {
                  const localDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
                  const dateVal = localDate.toISOString().slice(0, 19);
                  handleInputChange(rowIndex, fieldIndex, dateVal);
                } else {
                  handleInputChange(rowIndex, fieldIndex, "");
                }
                setTimeout(() => triggerValidation(rowIndex, fieldIndex), 50);
              }}
              placeholder="Select Date"
              fieldID={`date-${rowIndex}-${fieldIndex}`}
              id={`date-${rowIndex}-${fieldIndex}`}
              ref={commonProps.ref}
              maxDate={maxDate}
              disabled={(date) => {
                if (!date) return false;
                const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                return dateOnly > maxDate;
              }}
            />
          </div>
        );
      },

      text: () => {
        if (column.field === "No") {
          return <span className="mb-5">{value}</span>;
        }
        return (
          <Input
            {...inputProps}
            type="text"
            onBlur={() => handleInputBlur(rowIndex, fieldIndex)}
          />
        );
      }
    };

    return fieldRenderers[column.type] ? fieldRenderers[column.type]() : fieldRenderers.text();
  }, [errorCells, handleInputChange, getCellKey, connections, handleInputBlur, triggerValidation]);

  // Gets dialog content based on current state
  const getDialogContent = () => {
    const errorFreeRowsCount = errorFreeRows.length;
    const totalRows = editableData.length;
    const hasErrors = errorCells.size > 0;

    if (ignoreErrors && hasErrors) {
      return {
        title: "Import Valid Transactions",
        message: `Found ${totalRows - errorFreeRowsCount} ${pluralize(totalRows - errorFreeRowsCount, "record", "records")} with errors that will be skipped. Are you sure you wish to import ${errorFreeRowsCount} valid ${pluralize(errorFreeRowsCount, "transaction", "transactions")}?`,
        confirmText: `Import ${errorFreeRowsCount} ${pluralize(errorFreeRowsCount, "Transaction", "Transactions")}`
      };
    } else {
      return {
        title: "Transaction Import",
        message: `Are you sure you want to import all ${totalRows} ${pluralize(totalRows, "transaction", "transactions")}?`,
        confirmText: `Import All ${totalRows} ${pluralize(totalRows, "Transaction", "Transactions")}`
      };
    }
  };

  const dialogContent = getDialogContent();

  return (
    <div className="h-[95vh] flex flex-col space-y-3 lg:space-y-6 overflow-hidden scrollbar-hide">
      {/* Header */}
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-center flex-row justify-between">
          <h1 className="hidden lg:block text-3xl font-bold text-text-color mr-4 web-header-name">
            Transaction Import
          </h1>
          <div className="fixed right-6 flex justify-between items-center gap-3 z-500">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button onClick={() => setIgnoreErrors(prev => !prev)}>
                    <div className='f-row items-center justify-between gap-2'>
                      <Checkbox
                        checked={ignoreErrors}
                        disabled={errorCells.size === 0}
                        className={transactionImpClasses.checkbox}
                      />
                      <span className="font-medium text-base text-black">Ignore Errors</span>
                    </div>
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {errorCells.size > 0
                      ? `${errorFreeRows.length} out of ${editableData.length} rows are valid and can be imported`
                      : "No errors found"}
                  </p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      variant="secondary"
                      className={`${transactionImpClasses.button} ${transactionImpClasses.primaryButton}`}
                      onClick={handlePreviousError}
                      disabled={prevDisabled}
                    >
                      <FontAwesomeIcon icon={faChevronUp} />
                      Previous Error
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Go to Previous Error</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      variant="secondary"
                      className={`${transactionImpClasses.button} ${transactionImpClasses.primaryButton}`}
                      onClick={handleNextError}
                      disabled={nextDisabled}
                    >
                      <FontAwesomeIcon icon={faChevronDown} />
                      Next Error
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Go to Next Error</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="inline-block">
                    <Button
                      className={cn(transactionImpClasses.saveButton)}
                      onClick={() => setSaveDialog(true)}
                      disabled={
                        (!ignoreErrors && errorCells.size > 0) ||
                        errorFreeRows.length === 0
                      }
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      Save
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    {errorFreeRows.length === 0
                      ? "No valid rows to save"
                      : !ignoreErrors && errorCells.size > 0
                        ? "Fix all errors or enable 'Ignore Errors' to save valid rows"
                        : ignoreErrors && errorCells.size > 0
                          ? `Save ${errorFreeRows.length} valid rows`
                          : "Save all rows"}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>
      </div>

      {/* Main Card */}
      <Card className={transactionImpClasses.card}>
        <CardContent className="flex-1 flex flex-col overflow-hidden h-[100vh]">
          {apiError && (
            <div className="bg-red-50 border border-red-200 text-sahi-red px-4 py-3 sahi-radius mb-4">
              {apiError}
            </div>
          )}

          {editableData.length === 0 && (
            <div className="text-gray-600 mb-4 text-center py-8">
              No data loaded. Please upload a valid Excel file.
            </div>
          )}

          {editableData.length > 0 && (
            <VirtualizedTable
              data={editableData}
              columns={columns}
              errorCells={errorCells}
              cellErrors={cellErrors}
              currentError={currentError}
              getCellKey={getCellKey}
              renderInputField={renderInputField}
            />
          )}
        </CardContent>
      </Card>

      {/* Save Confirmation Dialog */}
      {saveDialog && (
        <Dialog open={saveDialog} onOpenChange={setSaveDialog}>
          <DialogContent className="w-full max-w-6xl pb-4 sahi-radius overflow-hidden">
            <DialogHeader className="dialog-header flex items-center justify-between px-4 pt-4">
              <div className="flex items-center gap-2">
                <DialogTitle className="font-semibold text-lg">
                  {dialogContent.title}
                </DialogTitle>
              </div>
              <DialogClose className="p-1 sahi-radius">
                <X className="h-5 w-5" />
              </DialogClose>
            </DialogHeader>

            <div className="px-4">
              {dialogContent.message}
            </div>

            <div className="w-fit !sahi-radius px-4">
              <div className="f-row gap-3 justify-between">
                <Button onClick={handleSave} className="!w-full lg:w-fit">
                  <Check className="h-4 w-4" />
                  {dialogContent.confirmText}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setSaveDialog(false)}
                  className="!w-full lg:w-fit cls-btn"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

export default CorrectExcelTransactions;